import { useEffect, useState } from 'react'
import FormPage from '../../../../Components/Form/Page/FormPage'
import useBriefContext from '../../../../Hooks/Brief/useBriefContext'
import getAdjacentSteps from '../../../../Logic/Form/Authorization/getAdjacentSteps'
import { LocationAttributes } from '../../../../Types/Location'
import BaseLocationInput from '../Base'
import InputView from '../../InputView'
import { LEADFEED_DEFAULT_RADIUS, SPACE_SEARCH_URL } from '../../../../Constants'
import { getDefaultLocationAttrs, LocationAttributesWithoutRadius } from '../Base/LocationInput'
import BizLoginBanner from '../../../../Components/BizLogin/BizLoginBanner'
import GeocodeErrorAlert from './GeocodeError'
import './LocationInput.less'
import './LocationInputAnimation.less'
import LoadingSpinner from '../../../../Components/LoadingSpinner'

type SpaceSearchLocationAttrs = Omit<LocationAttributes, 'currency' | 'country' | 'leadFeedCity'>

const getSpaceSearchURL = (attrs: SpaceSearchLocationAttrs): URL => {
  const url = new URL(SPACE_SEARCH_URL)
  const searchEntries = Object.entries(attrs).map<[string, string]>(([prop, val]) => [
    `q[${prop}]`,
    `${val}`,
  ])
  const searchParams = new URLSearchParams([['utf8', '✓'], ...searchEntries])
    .toString()
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']')
    .replace(/%20/g, '+')

  return new URL(`${url.origin}${url.pathname}?${searchParams}`)
}

const LocationInput: InputView = ({ path }) => {
  const { brief, dispatchBrief, loading, geocodeError } = useBriefContext()
  const [location, setLocation] = useState<LocationAttributesWithoutRadius | undefined>(
    getDefaultLocationAttrs(brief)
  )
  const [pageClassName, setPageClassName] = useState('')
  const [redirecting, setRedirecting] = useState(false)
  const [radius, setRadius] = useState<LocationAttributes['radius']>(
    brief.radius ?? LEADFEED_DEFAULT_RADIUS
  )
  useEffect(() => {
    if (loading || location === undefined) return

    setPageClassName('delay-map-animations')
  }, [loading])

  const { isStepComplete } = getAdjacentSteps(path, brief)

  const allLocationAttrs = { radius, ...(location && location) }
  const isLeadFeedReady = location?.leadFeedCity !== undefined
  const isSpaceSearchReady = isStepComplete(allLocationAttrs)
  const onContinue = () => dispatchBrief({ updateAttributes: allLocationAttrs })
  const navigateToSpaceSearch = () => {
    setRedirecting(true)
    const { lat, lon, radius, location } = allLocationAttrs
    if (lat !== undefined && lon !== undefined && radius !== undefined && location !== undefined) {
      return window.location.assign(getSpaceSearchURL({ lat, lon, radius, location }))
    }
    throw new Error(
      `Could not navigate to Space Search with location params:\n${JSON.stringify(
        allLocationAttrs
      )}`
    )
  }

  return (
    <FormPage
      path={path}
      metaTitle='Location - HeadBox'
      heading='What location works best?'
      alert={geocodeError && <GeocodeErrorAlert />}
      disableNav={redirecting}
      onContinue={onContinue}
      canContinue={isLeadFeedReady || isSpaceSearchReady}
      afterContinue={!isLeadFeedReady && isSpaceSearchReady ? navigateToSpaceSearch : undefined}
      className={`location-input leadfeed ${pageClassName}`}>
      {redirecting ? (
        <LoadingSpinner fadeIn={true} accessibleText='Redirecting you to venue search...' />
      ) : (
        <>
          <BizLoginBanner />
          <BaseLocationInput
            radius={radius}
            locationAttributes={location}
            setLocation={setLocation}
            setRadius={setRadius}
          />
        </>
      )}
    </FormPage>
  )
}

export default LocationInput
